import { useState } from 'react';

import { IconAngleDown, IconAngleUp } from '@iwoca/icons';
import { fetchPatchApplicationsAccountFileFeedback } from '@iwoca/lapi-client/edge';
import { Accordion } from '@iwoca/orion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classnames from 'classnames';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { FeedbackCard } from '../../documentFeedback/FeedbackCard';
import { TDocuments, TFeedbackReasonType } from '../../TradingDataHelper';

export const DocumentReview = ({
    document,
    onUpload,
    applicationId
}: {
    document: TDocuments[number];
    onUpload: (feedbackIds?: number[]) => void;
    applicationId: string;
    isRefetching: boolean;
}) => {
    const intl = useIntl();

    const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);

    const documentAttributes = document.attributes;
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const markedAsDone =
        !!documentAttributes.document_feedback &&
        documentAttributes.document_feedback.every(
            (feedback) => feedback.marked_as_done_by_customer
        );

    const onUploadReplacement = () => {
        const feedbackIds = documentAttributes.document_feedback?.map(
            (feedback) => feedback.id
        );
        onUpload(feedbackIds);
    };

    const onMarkAsDone = () => {
        setIsUpdatingDocument(true);
        markDocumentsAsDone();
    };

    const { mutate: markDocumentsAsDone } = useMutation({
        mutationFn: async () => {
            if (!documentAttributes.document_feedback) {
                throw Error('No document feedback to mark as done');
            }

            await Promise.all(
                documentAttributes.document_feedback.map((attribute) =>
                    fetchPatchApplicationsAccountFileFeedback({
                        feedbackId: attribute.id.toString(),
                        applicationId: applicationId,
                        body: {
                            marked_as_done_by_customer: !markedAsDone
                        }
                    })
                )
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['documents', stateKey]);
            setIsUpdatingDocument(false);
        }
    });

    const renderFeedbackReason = () => {
        const feedbacks = documentAttributes.document_feedback || [];

        const DOC_FEEDBACK_REASONS: Record<
            TFeedbackReasonType,
            React.ReactNode
        > = {
            ILLEGIBLE: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.illegible"
                    defaultMessage="We’re unable to accept this document because it's illegible. Please upload a new statement in PDF format that is of sufficient quality so that we can see the figures."
                />
            ),
            INVALID_DOCUMENT_FORMAT: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.invalid_document_format"
                    defaultMessage="We're unable to accept this document because it's not in PDF format. Please upload a new statement that is in PDF format."
                />
            ),
            PASSWORD_NOT_AVAILABLE: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.password_not_available"
                    defaultMessage="We're unable to accept this document because it's password protected. Please upload a new statement in PDF format that is not password protected, or share the password with us via email."
                />
            ),
            REFERENCES_MISSING: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.references_missing"
                    defaultMessage="We're unable to accept this document as it does not include the full transaction references. Please upload a new statement with complete, detailed transaction references in PDF format. You can find more information on the required references {faqLink}."
                    values={{
                        faqLink: (
                            <a
                                className="text-primary-40 hover:underline"
                                href={intl.formatMessage({
                                    id: '_.trading_data.uploaded_documents.document_review.references_missing.faq_link',
                                    defaultMessage: 'Enter turnover amount'
                                })}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FormattedMessage
                                    id="_.trading_data.uploaded_documents.document_review.references_missing.faq_link_text"
                                    defaultMessage="here"
                                />
                            </a>
                        )
                    }}
                />
            ),
            BALANCE_FIGURES_MISSING: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.balance_figures_missing"
                    defaultMessage="We're unable to accept this document as it does not include the balance figures. Please upload a new statement with the complete opening and closing account balance in PDF format."
                />
            ),
            MISSING_TRANSACTIONS: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.missing_transactions"
                    defaultMessage="We're unable to accept this document as it's missing transactions. Please upload a new statement that contains all transactions in PDF format."
                />
            ),
            MISSING_APPLICANT_NAME_AND_COMPANY_NAME: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.missing_applicant_name_and_company_name"
                    defaultMessage="We're unable to accept this document as it's missing your full name and company name. Please upload a new statement that contains this information in PDF format."
                />
            ),
            MISSING_SUMMARY_PAGE: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.missing_summary_page"
                    defaultMessage="We're unable to accept this document as it's missing the summary page of the account statement. Please upload a new statement that includes the first page with the bank's name and logo, full IBAN and your full name and the company name, in PDF format."
                />
            ),
            MISSING_LOGO_AND_IBAN: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.missing_logo_and_iban"
                    defaultMessage="We're unable to accept this document as it's missing the logo and full IBAN. Please upload a new statement that includes this information in PDF format. Alternatively, you can send this to us directly via Open Banking."
                />
            ),
            SCREENSHOT: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.screenshot"
                    defaultMessage="We’re unable to accept this document because it's a screenshot of your statement. Please upload a new statement that is exported directly from your online banking in PDF format. Alternatively, you can send this to us directly via Open Banking."
                />
            ),
            ACTIVE_LINKS: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.active_links"
                    defaultMessage="We're unable to accept this document because it has active links in the booking lines. Please upload a new statement without the active links in PDF format. Alternatively, you can send this to us directly via Open Banking."
                />
            ),
            INCORRECT_ORDER: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.incorrect_order"
                    defaultMessage="We're unable to accept this document because the transactions are not in date order. Please upload a new statement with transactions in chronological order in PDF format. Alternatively, you can send this to us directly via Open Banking."
                />
            ),
            BS_WITHOUT_ACTIVE_LINKS: (
                <FormattedMessage
                    id="_.trading_data.uploaded_documents.document_review.bs_without_active_links"
                    defaultMessage="We're unable to accept this document because it has active links in it. Please upload a new statement in PDF format that does not contain active links."
                />
            )
        };

        const validFeedbacks = feedbacks.filter(
            (request) => request.feedback_type in DOC_FEEDBACK_REASONS
        );

        if (validFeedbacks.length === 0) return null;

        return validFeedbacks.length > 1 ? (
            <ul className="mb-l list-disc pl-xl text-left">
                {validFeedbacks.map((request) => (
                    <li
                        key={request.id}
                        className="mb-s"
                        data-testid="feedback-type"
                    >
                        {DOC_FEEDBACK_REASONS[request.feedback_type]}
                    </li>
                ))}
            </ul>
        ) : (
            <div className="mb-l" data-testid="feedback-type">
                {DOC_FEEDBACK_REASONS[validFeedbacks[0]?.feedback_type]}
            </div>
        );
    };

    return (
        <Accordion.Item
            defaultOpen
            variant="secondary"
            size="small"
            className="!rounded-none max-xs:mb-l"
        >
            {({ open }) => (
                <>
                    <Accordion.Header
                        className={classnames(
                            'rounded-none border-none',
                            markedAsDone ? 'bg-structure-100' : 'bg-core-95'
                        )}
                    >
                        <Accordion.Title>
                            {open ? (
                                <IconAngleUp
                                    className="[&_path]:fill-primary-10"
                                    fontSize={20}
                                />
                            ) : (
                                <IconAngleDown
                                    className="[&_path]:fill-primary-10"
                                    fontSize={20}
                                />
                            )}
                            <div className="flex w-full items-center justify-between">
                                <span>{documentAttributes.document_name}</span>
                                <span className="pr-l">
                                    <FormattedDate
                                        value={
                                            new Date(
                                                documentAttributes.uploaded_at
                                            )
                                        }
                                        day="2-digit"
                                        month="short"
                                        year="numeric"
                                    />
                                </span>
                            </div>
                        </Accordion.Title>
                    </Accordion.Header>
                    <Accordion.Content
                        className={classnames(
                            '!border-2 border-core-95 bg-structure-100',
                            open && 'pt-l'
                        )}
                    >
                        <FeedbackCard
                            isMarkedAsDone={markedAsDone}
                            isMarkingAsDone={isUpdatingDocument}
                            onMarkAsDone={onMarkAsDone}
                            warningText={
                                <FormattedMessage
                                    id="_.trading_data.uploaded_documents.document_review.warning_text"
                                    defaultMessage="We're not able to accept this document"
                                />
                            }
                            instructionText={
                                <FormattedMessage
                                    id="_.trading_data.uploaded_documents.document_review.upload_instruction"
                                    defaultMessage="- please upload a replacement in PDF"
                                />
                            }
                            uploadText={
                                <FormattedMessage
                                    id="_.trading_data.uploaded_documents.document_review.upload_button"
                                    defaultMessage="Upload replacement"
                                />
                            }
                            onUpload={onUploadReplacement}
                            feedbackCategory="account_file_feedback"
                        >
                            {renderFeedbackReason()}
                        </FeedbackCard>
                    </Accordion.Content>
                </>
            )}
        </Accordion.Item>
    );
};
